import api from '@/services/utils/apiInstance.ts'
import { buildQuery } from '@/services/utils/request.ts';
import { mapIdentityDuplicateReview } from '@/services/identityDuplicateReview/identityDuplicateReviewService.ts';

import type {
  UserResponse,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  B2CUser,
  KycResponse,
  KycResponseApi
} from '@/types/services/user/user.type.ts';
import type { PaginatedQuery } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';

function mapKycResponse(kyc: KycResponseApi | null): KycResponse | null {
  if (!kyc) return null

  return {
    state: kyc.state,
    status: kyc.status,
    portal_url: kyc.portal_url,
    risk: kyc.risk,
    verifiedData: kyc.verified_data ? {
      identity: {
        firstName: kyc.verified_data.identity.first_name,
        lastName: kyc.verified_data.identity.last_name,
        birthDate: kyc.verified_data.identity.birth_date,
        birthPlace: kyc.verified_data.identity.birth_place,
        gender: kyc.verified_data.identity.gender,
        nationality: kyc.verified_data.identity.nationality
      },
      documentDetails: {
        documentType: kyc.verified_data.document_details.document_type,
        documentNumber: kyc.verified_data.document_details.document_number,
        issuingCountry: kyc.verified_data.document_details.issuing_country,
        issueDate: kyc.verified_data.document_details.issue_date,
        expiryDate: kyc.verified_data.document_details.expiry_date
      },
      addressVerification: {
        fullAddress: kyc.verified_data.address_verification.full_address
      },
      amlCheck: {
        pepStatus: kyc.verified_data.aml_check.pep_status,
        sanctionsList: kyc.verified_data.aml_check.sanctions_list,
        adverseMedia: kyc.verified_data.aml_check.adverse_media
      }
    } : null
  }
}

function mapDuplicateReview(data: UserResponse['duplicate_review']): User['duplicateReview'] {
  return data ? mapIdentityDuplicateReview(data) : null
}

class UserService {
  async getPaginatedAdmin(params: PaginatedQuery): Promise<PaginatedResponse<User>> {
    const query = buildQuery(params)
    const response = await api.get(`/user/admin/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((user: UserResponse) => ({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        usageName: user.usage_name ?? null,
        phone: response.data.phone,
        role: user.role,
        status: user.status,
        kyc: mapKycResponse(user.kyc)
      }))
    }
  }

  async getAdmin(userId: number): Promise<User> {
    const response = await api.get(`/user/admin/${userId}`)
    return {
      id: response.data.id,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      usageName: response.data.usage_name ?? null,
      phone: response.data.phone,
      role: response.data.role,
      status: response.data.status,
      kyc: mapKycResponse(response.data.kyc),
      duplicateReview: mapDuplicateReview(response.data.duplicate_review)
    }
  }

  async createAdmin(userData: CreateUserRequest): Promise<void> {
    await api.post('/user/admin', {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    });
  }

  async updateAdmin(userId: number, userData: UpdateUserRequest): Promise<void> {
    await api.put(`/user/admin/${userId}`, {
      firstName: userData.firstName,
      lastName: userData.lastName
    });
  }

  async updateAdminStatus(userId: number, status: string): Promise<void> {
    await api.patch(`/user/admin/${userId}/${status}`)
  }

  // Generates a fresh password-creation token and resends the invitation email. The backend
  // refuses (error surfaced via the default toast handling) if the user doesn't exist, doesn't
  // have the admin role, or has already set their password.
  async resendAdminInvitation(userId: number): Promise<void> {
    await api.post(`/user/admin/${userId}/resend-invitation`)
  }

  async getPaginatedDam(params: PaginatedQuery): Promise<PaginatedResponse<User>> {
    const query = buildQuery(params)
    const response = await api.get(`/user/dam/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((user: UserResponse) => ({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        usageName: user.usage_name ?? null,
        role: user.role,
        status: user.status,
        kyc: mapKycResponse(user.kyc)
      }))
    }
  }

  async getDam(userId: number): Promise<User> {
    const response = await api.get(`/user/dam/${userId}`)
    return {
      id: response.data.id,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      usageName: response.data.usage_name ?? null,
      phone: response.data.phone,
      role: response.data.role,
      status: response.data.status,
      kyc: mapKycResponse(response.data.kyc),
      duplicateReview: mapDuplicateReview(response.data.duplicate_review)
    }
  }

  async createDam(userData: CreateUserRequest): Promise<void> {
    await api.post('/user/dam', {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
    });
  }

  async updateDam(userId: number, userData: UpdateUserRequest): Promise<void> {
    await api.put(`/user/dam/${userId}`, {
      firstName: userData.firstName,
      lastName: userData.lastName
    });
  }

  async updateDamStatus(userId: number, status: string): Promise<void> {
    await api.patch(`/user/dam/${userId}/${status}`)
  }

  // See resendAdminInvitation — same behavior, DAM role.
  async resendDamInvitation(userId: number): Promise<void> {
    await api.post(`/user/dam/${userId}/resend-invitation`)
  }

  async getPaginatedUser(params: PaginatedQuery): Promise<PaginatedResponse<User>> {
    const query = buildQuery(params)
    const response = await api.get(`/user/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((user: UserResponse) => ({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        usageName: user.usage_name ?? null,
        role: user.role,
        status: user.status,
        kyc: mapKycResponse(user.kyc),
      }))
    }
  }

  async getUser(userId: number): Promise<User> {
    const response = await api.get(`/user/${userId}`)
    return {
      id: response.data.id,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      usageName: response.data.usage_name ?? null,
      phone: response.data.phone,
      role: response.data.role,
      status: response.data.status,
      kyc: mapKycResponse(response.data.kyc),
      duplicateReview: mapDuplicateReview(response.data.duplicate_review)
    }
  }

  async getB2CUser(userId: number): Promise<B2CUser> {
    const response = await api.get(`/user/${userId}`)
    return {
      id: response.data.id,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      usageName: response.data.usage_name ?? null,
      phone: response.data.phone,
      role: response.data.role,
      status: response.data.status,
      kyc: mapKycResponse(response.data.kyc),
      duplicateReview: mapDuplicateReview(response.data.duplicate_review),
      profileType: response.data.profile_type,
      accountActivated: response.data.account_activated ?? false
    }
  }

  // Generates a fresh activation link and resends the activation email. `isWeb` selects which
  // link is sent: `false` (default) sends the mobile app link, `true` the Klintt web link.
  // Backend refuses (surfaced via the default toast handling) if the client doesn't exist (404)
  // or the account is already activated (409) — callers should hide/disable the button using
  // `accountActivated` from `getB2CUser` rather than relying on this error.
  async resendActivation(userId: number, isWeb: boolean = false): Promise<void> {
    await api.post(`/user/${userId}/resend-activation`, null, {
      params: { is_web: isWeb }
    })
  }

  async updateUserStatus(userId: number, status: string): Promise<void> {
    await api.patch(`/user/${userId}/${status}`)
  }

  async createKycRequest(userId: number): Promise<void> {
    await api.post(`/user/kyc-request/${userId}`)
  }

  async approveKyc(userId: number): Promise<void> {
    await api.put(`/user/approve-kyc/${userId}`)
  }

  async refuseKyc(userId: number, reason: string) {
    await api.put(`/user/refused-kyc/${userId}`, reason)
  }

  async anonymize(userId: number, reason: string): Promise<void> {
    await api.delete('/user/anonymize', {
      data: {
        userId: userId,
        reasonForAnonymization: reason
      }
    })
  }
}

export default new UserService();
